import { useState, useEffect, useCallback } from "react";
import {
  CalendarDays,
  Clock,
  Timer,
  Edit2,
  Plus,
  Copy,
  Trash2,
  Check,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  User,
  CopyPlus,
  CopyCheck,
  AlertTriangle,
  FileDown,
  Coffee,
} from "lucide-react";
import { Btn, Modal, FormField, Badge, inputCls } from "../ui";
import { useToast } from "../../contexts/NotificationContext";
import {
  getSchedulePlans,
  initSchedulePlans,
  createSchedulePlan,
  updateSchedulePlan,
  deleteSchedulePlan,
  duplicateSchedulePlan,
  duplicateSchedulePlanWeek,
  upsertSchedulePlanSlot,
  deleteSchedulePlanSlot,
  validateSchedulePlan,
  unvalidateSchedulePlan,
} from "../../api";

// ─── Constantes ──────────────────────────────────────────────────────────────

const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const JOURS_LONG = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];
const MOIS_COURT = [
  "Jan",
  "Fév",
  "Mar",
  "Avr",
  "Mai",
  "Jun",
  "Jul",
  "Aoû",
  "Sep",
  "Oct",
  "Nov",
  "Déc",
];

function calcDuree(debut, fin, pauseMin = 0) {
  if (!debut || !fin) return "";
  const [hd, md] = debut.split(":").map(Number);
  const [hf, mf] = fin.split(":").map(Number);
  const total = hf * 60 + mf - (hd * 60 + md) - parseInt(pauseMin || 0);
  if (total <= 0) return "—";
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
}

function fmtDateFr(d) {
  if (!d) return "";
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
}

function toInputDate(d) {
  if (!d) return "";
  return new Date(d).toISOString().slice(0, 10);
}

function calcTotalCycle(slots, nbSemaines) {
  let total = 0;
  for (const s of slots) {
    const duree = calcDuree(s.startTime, s.endTime, s.pauseMinutes);
    if (duree && duree !== "—") {
      const [h, m] = duree
        .replace("h", ":")
        .split(":")
        .map((v) => parseInt(v) || 0);
      total += h * 60 + m;
    }
  }
  const h = Math.floor(total / 60);
  const m = total % 60;
  const totalStr = m > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
  const moy = nbSemaines > 0 ? Math.round(total / nbSemaines) : 0;
  const moyH = Math.floor(moy / 60);
  const moyM = moy % 60;
  const moyStr =
    moyM > 0 ? `${moyH}h${String(moyM).padStart(2, "0")}` : `${moyH}h`;
  return { totalStr, moyStr };
}

// ─── ModalCreneau ────────────────────────────────────────────────────────────

function ModalCreneau({
  semIndex,
  jourIndex,
  creneau,
  onSave,
  onDelete,
  onClose,
}) {
  const [form, setForm] = useState({
    startTime: creneau?.startTime || "",
    endTime: creneau?.endTime || "",
    pauseMinutes: creneau?.pauseMinutes ?? 60,
  });
  const duree = calcDuree(form.startTime, form.endTime, form.pauseMinutes);

  return (
    <Modal
      title={`Créneau — ${JOURS_LONG[jourIndex]} · Semaine ${semIndex + 1}`}
      size="sm"
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          <Btn variant="secondary" size="sm" onClick={onClose}>
            Annuler
          </Btn>
          <Btn
            variant="primary"
            size="sm"
            disabled={!form.startTime || !form.endTime}
            onClick={() => onSave(form)}
          >
            <Check size={13} /> Enregistrer
          </Btn>
        </div>
      }
    >
      <div className="space-y-4">
        <FormField label="Heure de début" required>
          <input
            type="time"
            className={inputCls}
            value={form.startTime}
            onChange={(e) =>
              setForm((f) => ({ ...f, startTime: e.target.value }))
            }
          />
        </FormField>
        <FormField label="Heure de fin" required>
          <input
            type="time"
            className={inputCls}
            value={form.endTime}
            onChange={(e) =>
              setForm((f) => ({ ...f, endTime: e.target.value }))
            }
          />
        </FormField>
        <FormField label="Pause (minutes)">
          <input
            type="number"
            className={inputCls}
            min={0}
            max={120}
            step={5}
            value={form.pauseMinutes}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                pauseMinutes: parseInt(e.target.value) || 0,
              }))
            }
          />
        </FormField>
        {form.startTime && form.endTime && (
          <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
            <Clock size={12} className="text-primary shrink-0" />
            <span>
              Durée effective :{" "}
              <strong className="text-gray-700">{duree} travaillées</strong>
            </span>
          </div>
        )}
        {creneau && (
          <div className="border-t border-gray-100 pt-3">
            <Btn
              variant="ghost"
              size="sm"
              className="text-red-400 hover:text-red-600 hover:bg-red-50 w-full justify-center"
              onClick={onDelete}
            >
              <Trash2 size={13} /> Effacer ce créneau
            </Btn>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── ModalDupliquerPlan ───────────────────────────────────────────────────────

function ModalDupliquerPlan({ plan, onConfirm, onClose }) {
  const [form, setForm] = useState({
    nom: `${plan.nom} (copie)`,
    dateDebut: "",
    dateFin: "",
  });
  const dateError =
    form.dateDebut && form.dateFin && form.dateFin < form.dateDebut;
  return (
    <Modal
      title="Dupliquer le plan"
      size="sm"
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          <Btn variant="secondary" size="sm" onClick={onClose}>
            Annuler
          </Btn>
          <Btn
            variant="primary"
            size="sm"
            disabled={!form.nom || !!dateError}
            onClick={() => onConfirm(form)}
          >
            <Copy size={13} /> Dupliquer
          </Btn>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
          Tous les horaires de tous les employés seront copiés dans le nouveau
          plan.
        </p>
        <FormField label="Nom du nouveau plan" required>
          <input
            className={inputCls}
            value={form.nom}
            onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
          />
        </FormField>
        <FormField label="Date de début">
          <input
            type="date"
            className={inputCls}
            value={form.dateDebut}
            onChange={(e) =>
              setForm((f) => ({ ...f, dateDebut: e.target.value }))
            }
          />
        </FormField>
        <FormField label="Date de fin">
          <input
            type="date"
            className={inputCls}
            value={form.dateFin}
            onChange={(e) =>
              setForm((f) => ({ ...f, dateFin: e.target.value }))
            }
          />
        </FormField>
        {dateError && (
          <p className="text-xs text-red-500">
            La date de fin doit être après la date de début.
          </p>
        )}
      </div>
    </Modal>
  );
}

// ─── Helper chevauchement ────────────────────────────────────────────────────

function findOverlap(dateDebut, dateFin, plans, excludeId = null) {
  if (!dateDebut || !dateFin) return null;
  const a = new Date(dateDebut);
  const b = new Date(dateFin);
  return (
    plans.find(
      (p) =>
        p.id !== excludeId &&
        !p.isReference &&
        p.dateDebut &&
        p.dateFin &&
        a <= new Date(p.dateFin) &&
        b >= new Date(p.dateDebut),
    ) ?? null
  );
}

// ─── ModalNouveauPlan ─────────────────────────────────────────────────────────

function ModalNouveauPlan({ onConfirm, onClose, existingPlans = [] }) {
  const [form, setForm] = useState({
    nom: "",
    isReference: false,
    nbSemaines: 4,
    dateDebut: "",
    dateFin: "",
  });
  const dateError =
    form.dateDebut && form.dateFin && form.dateFin < form.dateDebut;
  const overlapPlan =
    !form.isReference &&
    !dateError &&
    findOverlap(form.dateDebut, form.dateFin, existingPlans);
  return (
    <Modal
      title="Nouveau plan"
      size="sm"
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          <Btn variant="secondary" size="sm" onClick={onClose}>
            Annuler
          </Btn>
          <Btn
            variant="primary"
            size="sm"
            disabled={!form.nom || !!dateError || !!overlapPlan}
            onClick={() => onConfirm(form)}
          >
            <Plus size={13} /> Créer
          </Btn>
        </div>
      }
    >
      <div className="space-y-4">
        <FormField label="Nom du plan" required>
          <input
            className={inputCls}
            placeholder="Ex : Planning été 2026"
            value={form.nom}
            onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
          />
        </FormField>
        <FormField label="Cycle (semaines)">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden w-fit">
            {[1, 2, 3, 4].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setForm((f) => ({ ...f, nbSemaines: n }))}
                className={`px-4 py-1.5 text-sm font-medium transition-colors border-r border-gray-200 last:border-0 ${form.nbSemaines === n ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
              >
                {n}S
              </button>
            ))}
          </div>
        </FormField>
        {!form.isReference && (
          <>
            <FormField label="Date de début">
              <input
                type="date"
                className={inputCls}
                value={form.dateDebut}
                onChange={(e) =>
                  setForm((f) => ({ ...f, dateDebut: e.target.value }))
                }
              />
            </FormField>
            <FormField label="Date de fin">
              <input
                type="date"
                className={inputCls}
                value={form.dateFin}
                onChange={(e) =>
                  setForm((f) => ({ ...f, dateFin: e.target.value }))
                }
              />
            </FormField>
            {dateError && (
              <p className="text-xs text-red-500">
                La date de fin doit être après la date de début.
              </p>
            )}
            {overlapPlan && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <AlertTriangle size={13} className="text-amber-500 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700">
                  Ces dates chevauchent <span className="font-medium">«&nbsp;{overlapPlan.nom}&nbsp;»</span>{" "}
                  ({fmtDateFr(overlapPlan.dateDebut)} → {fmtDateFr(overlapPlan.dateFin)}).
                  Ajustez les dates ou supprimez l'autre plan.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── GrilleHoraires ──────────────────────────────────────────────────────────

function GrilleHoraires({
  plan,
  employeeId,
  onCellClick,
  onDupliquerSemaine,
  onDupliquerToutCycle,
}) {
  const semaines = Array.from({ length: plan.nbSemaines }, (_, i) => i + 1);

  function getSlot(weekNumber, dayOfWeek) {
    return (
      plan.slots?.find(
        (s) =>
          s.employeeId === employeeId &&
          s.weekNumber === weekNumber &&
          s.dayOfWeek === dayOfWeek,
      ) ?? null
    );
  }

  const s1HasSlots = JOURS.some((_, ji) => getSlot(1, ji + 1));

  function calcTotalSemaine(sem) {
    let total = 0;
    JOURS.forEach((_, ji) => {
      const s = getSlot(sem, ji + 1);
      if (s?.startTime && s?.endTime) {
        const [h1, m1] = s.startTime.split(":").map(Number);
        const [h2, m2] = s.endTime.split(":").map(Number);
        total += h2 * 60 + m2 - (h1 * 60 + m1) - (s.pauseMinutes || 0);
      }
    });
    if (total <= 0) return null;
    const h = Math.floor(total / 60);
    const m = total % 60;
    return m > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
  }

  return (
    <div className="overflow-x-auto">
      <table
        className="border-collapse text-sm"
        style={{ minWidth: `${80 + JOURS.length * 110}px` }}
      >
        <thead>
          <tr>
            <th className="w-20 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wide pb-2 pr-2" />
            {JOURS.map((j, ji) => (
              <th
                key={ji}
                className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide pb-2 px-1 text-center min-w-[100px]"
              >
                {j}
              </th>
            ))}
            <th className="w-16 text-[10px] font-semibold text-gray-400 uppercase tracking-wide pb-2 text-center">
              Total
            </th>
            <th className="w-28 pb-2" />
          </tr>
        </thead>
        <tbody>
          {semaines.map((sem) => {
            const totalSem = calcTotalSemaine(sem);
            return (
              <tr
                key={sem}
                className={sem > 1 ? "border-t border-gray-100" : ""}
              >
                <td className="py-1.5 pr-2 align-middle">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                    S{sem}
                  </span>
                </td>
                {JOURS.map((_, ji) => {
                  const slot = getSlot(sem, ji + 1);
                  return (
                    <td key={ji} className="px-1 py-1">
                      <button
                        onClick={() => onCellClick(sem - 1, ji, slot)}
                        className={`w-full min-h-[52px] rounded-lg border text-xs transition-all focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                          slot
                            ? "bg-primary/8 border-primary/25 text-primary-dark hover:bg-primary/15 hover:border-primary/40"
                            : "bg-gray-50 border-dashed border-gray-200 text-gray-300 hover:bg-gray-100 hover:border-gray-300"
                        }`}
                      >
                        {slot ? (
                          <div className="flex flex-col items-center gap-0.5 py-1 px-1">
                            <span className="font-semibold text-[11px] leading-tight">
                              {slot.startTime}–{slot.endTime}
                            </span>
                            {slot.pauseMinutes > 0 && (
                              <span className="text-[10px] opacity-70 flex items-center gap-0.5">
                                <Coffee size={9} /> {slot.pauseMinutes} min
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-lg leading-none">+</span>
                        )}
                      </button>
                    </td>
                  );
                })}
                {/* Total semaine */}
                <td className="px-2 py-1 align-middle text-center">
                  {totalSem ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary bg-primary/8 rounded-md px-2 py-1 whitespace-nowrap">
                      <Clock size={10} />
                      {totalSem}
                    </span>
                  ) : (
                    <span className="text-[11px] text-gray-300">—</span>
                  )}
                </td>
                {/* Actions semaine */}
                <td className="px-1 py-1 align-middle">
                  <div className="flex flex-col gap-1">
                    {sem < plan.nbSemaines && (
                      <button
                        onClick={() => onDupliquerSemaine(sem)}
                        title={`Copier S${sem} → S${sem + 1}`}
                        className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-primary transition-colors px-2 py-1 rounded hover:bg-primary/5"
                      >
                        <CopyPlus size={11} />
                        <span>→S{sem + 1}</span>
                      </button>
                    )}
                    {sem === 1 && plan.nbSemaines > 1 && s1HasSlots && (
                      <button
                        onClick={onDupliquerToutCycle}
                        title="Appliquer S1 à toutes les semaines du cycle"
                        className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-accent-dark transition-colors px-2 py-1 rounded hover:bg-accent/5"
                      >
                        <CopyCheck size={11} />
                        <span>→ tout</span>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── PlanTimeline ─────────────────────────────────────────────────────────────

function PlanTimeline({ plans, activePlanId }) {
  const anneeActuelle = new Date().getFullYear();
  const [annee, setAnnee] = useState(anneeActuelle);
  const debutPeriode = new Date(annee, 0, 1);
  const finPeriode = new Date(annee, 11, 31);
  const today = new Date();

  function pct(date) {
    const d = new Date(date);
    return Math.min(
      100,
      Math.max(0, ((d - debutPeriode) / (finPeriode - debutPeriode)) * 100),
    );
  }

  const overrides = plans.filter(
    (p) => !p.isReference && p.dateDebut && p.dateFin,
  );
  const refPlan = plans.find((p) => p.isReference);
  const refHasDates = refPlan?.dateDebut || refPlan?.dateFin;
  const refLeft = refPlan?.dateDebut ? pct(refPlan.dateDebut) : 0;
  const refRight = refPlan?.dateFin ? pct(refPlan.dateFin) : 100;
  const refStyle = refHasDates
    ? { left: `${refLeft}%`, width: `${Math.max(0, refRight - refLeft)}%` }
    : { left: 0, right: 0 };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div
        className="flex items-center justify-between px-5 py-3 border-b border-gray-100"
        style={{
          background:
            "linear-gradient(to right, rgb(var(--color-primary) / 0.05), transparent)",
        }}
      >
        <div className="flex items-center gap-2">
          <CalendarDays size={14} className="text-primary" />
          <span className="font-semibold text-sm text-gray-800">
            Couverture des plannings
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Btn variant="ghost" size="sm" onClick={() => setAnnee((a) => a - 1)}>
            <ChevronLeft size={14} />
          </Btn>
          <span className="text-xs text-gray-600 font-medium w-10 text-center">
            {annee}
          </span>
          <Btn variant="ghost" size="sm" onClick={() => setAnnee((a) => a + 1)}>
            <ChevronRight size={14} />
          </Btn>
        </div>
      </div>
      <div className="px-5 py-4">
        <div className="relative" style={{ minWidth: "500px" }}>
          <div className="grid grid-cols-12 mb-1">
            {MOIS_COURT.map((m, i) => (
              <span
                key={i}
                className="text-[10px] text-gray-400 uppercase tracking-wide text-center"
              >
                {m}
              </span>
            ))}
          </div>
          <div className="h-px bg-gray-200 mb-3" />
          <div className="relative h-14">
            {refPlan && (Math.max(0, refRight - refLeft) > 0 || !refHasDates) && (
              <div
                className="absolute top-2 bottom-2 rounded-lg bg-primary/15 border border-primary/20 flex items-center px-3 overflow-hidden"
                style={refStyle}
                title={refPlan.dateFin ? `${refPlan.nom} : ${fmtDateFr(refPlan.dateDebut)} → ${fmtDateFr(refPlan.dateFin)}` : refPlan.dateDebut ? `${refPlan.nom} : à partir du ${fmtDateFr(refPlan.dateDebut)}` : `${refPlan.nom} (permanent)`}
              >
                <span className="text-[11px] font-medium text-primary truncate flex items-center gap-1.5">
                  <RotateCcw size={10} />
                  {refPlan.nom}
                </span>
              </div>
            )}
            {overrides.map((p) => {
              const left = pct(p.dateDebut);
              const width = pct(p.dateFin) - left;
              if (width <= 0) return null;
              return (
                <div
                  key={p.id}
                  title={`${p.nom} : ${fmtDateFr(p.dateDebut)} → ${fmtDateFr(p.dateFin)}`}
                  className={`absolute top-0.5 bottom-0.5 rounded-lg flex items-center px-2 border shadow-sm overflow-hidden ${p.id === activePlanId ? "bg-accent/40 border-accent/60" : "bg-accent/20 border-accent/40"}`}
                  style={{ left: `${left}%`, width: `${width}%` }}
                >
                  <span className="text-[10px] font-semibold text-accent-dark truncate">
                    {p.nom}
                  </span>
                </div>
              );
            })}
            {today.getFullYear() === annee && (
              <div
                className="absolute top-0 bottom-0 w-px bg-red-400 z-10"
                style={{ left: `${pct(today)}%` }}
              >
                <div className="absolute -top-1 -translate-x-1/2">
                  <span className="block w-2 h-2 rounded-full bg-red-400" />
                </div>
                <span className="absolute top-full left-1 text-[9px] text-red-400 whitespace-nowrap mt-0.5">
                  Auj.
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 mt-3">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-2 rounded bg-primary/30 inline-block" />
              <span className="text-[10px] text-gray-500">Référence</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />
              <span className="text-[10px] text-gray-500">Aujourd'hui</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── GlobalPlanningTab — composant principal ──────────────────────────────────

export default function GlobalPlanningTab({ structureId, structureName = '', employees = [] }) {
  const toast = useToast();

  const [plans, setPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [loading, setLoading] = useState(true);
  const [initLoading, setInitLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null); // 'nouveau'|'dupliquer'|'confirmerSuppr'|'copierEmp'
  const [slotModal, setSlotModal] = useState(null);
  const [editNom, setEditNom] = useState(false);
  const [nomEdit, setNomEdit] = useState("");
  const [copyTargetEmpId, setCopyTargetEmpId] = useState("");
  const [pdfWeekPicker, setPdfWeekPicker] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) ?? null;

  // ── Chargement ────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!structureId) return;
    setLoading(true);
    try {
      const res = await getSchedulePlans(structureId);
      const { plans: loaded, activePlanId: aId } = res.data;
      setPlans(loaded);
      setActivePlanId(aId);
      setSelectedPlanId((s) =>
        s && loaded.find((p) => p.id === s) ? s : (loaded[0]?.id ?? null),
      );
    } catch {
      toast.error("Erreur chargement du planning");
    } finally {
      setLoading(false);
    }
  }, [structureId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleInit() {
    setInitLoading(true);
    try {
      const res = await initSchedulePlans(structureId);
      const plan = res.data.plan;
      setPlans([plan]);
      setActivePlanId(plan.id);
      setSelectedPlanId(plan.id);
      toast.success("Plan de référence créé");
    } catch {
      toast.error("Erreur création du plan de référence");
    } finally {
      setInitLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [load]);

  // Utilise l'ID du premier employé (primitif string) au lieu de la référence du tableau
  const firstEmpId = employees[0]?.id;
  useEffect(() => {
    if (!selectedEmpId && firstEmpId) setSelectedEmpId(firstEmpId);
  }, [firstEmpId, selectedEmpId]);

  const selectedPlanId_stable = selectedPlan?.id;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedPlan) setNomEdit(selectedPlan.nom);
  }, [selectedPlanId_stable]);

  // ── Mise à jour plan ──────────────────────────────────────────────────────

  async function handleUpdate(data) {
    if (!selectedPlanId) return;
    // Validation dates
    const next = { ...selectedPlan, ...data };
    if (next.dateDebut && next.dateFin && next.dateFin < next.dateDebut) {
      toast.error("La date de fin doit être après la date de début");
      return;
    }
    // Vérification chevauchement (uniquement pour les plans override)
    if (!next.isReference && (data.dateDebut !== undefined || data.dateFin !== undefined)) {
      const conflict = findOverlap(next.dateDebut, next.dateFin, plans, selectedPlanId);
      if (conflict) {
        toast.warning(`Les dates chevauchent « ${conflict.nom} » (${fmtDateFr(conflict.dateDebut)} → ${fmtDateFr(conflict.dateFin)})`);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await updateSchedulePlan(selectedPlanId, data);
      setPlans((ps) => ps.map((p) => (p.id === selectedPlanId ? res.data : p)));
    } catch {
      toast.error("Erreur mise à jour du plan");
    } finally {
      setSaving(false);
    }
  }

  function handleNomBlur() {
    setEditNom(false);
    if (nomEdit.trim() && nomEdit !== selectedPlan?.nom)
      handleUpdate({ nom: nomEdit.trim() });
  }

  // ── Créneaux ──────────────────────────────────────────────────────────────

  async function handleSlotSave(form) {
    if (!selectedPlanId || !selectedEmpId) return;
    setSaving(true);
    try {
      const res = await upsertSchedulePlanSlot(selectedPlanId, {
        employeeId: selectedEmpId,
        weekNumber: slotModal.semIndex + 1,
        dayOfWeek: slotModal.jourIndex + 1,
        startTime: form.startTime,
        endTime: form.endTime,
        pauseMinutes: form.pauseMinutes,
      });
      setPlans((ps) =>
        ps.map((p) => {
          if (p.id !== selectedPlanId) return p;
          const slots = (p.slots || []).filter(
            (s) =>
              !(
                s.employeeId === selectedEmpId &&
                s.weekNumber === slotModal.semIndex + 1 &&
                s.dayOfWeek === slotModal.jourIndex + 1
              ),
          );
          return { ...p, slots: [...slots, res.data] };
        }),
      );
      toast.success("Créneau enregistré");
    } catch {
      toast.error("Erreur enregistrement du créneau");
    } finally {
      setSaving(false);
      setSlotModal(null);
    }
  }

  async function handleSlotDelete() {
    if (!slotModal?.slot) {
      setSlotModal(null);
      return;
    }
    setSaving(true);
    try {
      await deleteSchedulePlanSlot(selectedPlanId, slotModal.slot.id);
      setPlans((ps) =>
        ps.map((p) =>
          p.id !== selectedPlanId
            ? p
            : {
                ...p,
                slots: (p.slots || []).filter(
                  (s) => s.id !== slotModal.slot.id,
                ),
              },
        ),
      );
      toast.success("Créneau supprimé");
    } catch {
      toast.error("Erreur suppression du créneau");
    } finally {
      setSaving(false);
      setSlotModal(null);
    }
  }

  // ── Dupliquer semaine S→S+1 ───────────────────────────────────────────────

  async function handleDupliquerSemaine(fromWeek) {
    if (!selectedPlanId || !selectedEmpId) return;
    setSaving(true);
    try {
      const res = await duplicateSchedulePlanWeek(selectedPlanId, {
        employeeId: selectedEmpId,
        fromWeek,
      });
      const newSlots = res.data;
      const toWeek = fromWeek + 1;
      setPlans((ps) =>
        ps.map((p) => {
          if (p.id !== selectedPlanId) return p;
          const slots = (p.slots || []).filter(
            (s) => !(s.employeeId === selectedEmpId && s.weekNumber === toWeek),
          );
          return { ...p, slots: [...slots, ...newSlots] };
        }),
      );
      toast.success(`Semaine ${fromWeek} copiée vers S${toWeek}`);
    } catch {
      toast.error("Erreur duplication de semaine");
    } finally {
      setSaving(false);
    }
  }

  // ── Dupliquer S1 → tout le cycle ──────────────────────────────────────────

  async function handleDupliquerToutCycle() {
    if (!selectedPlanId || !selectedEmpId || !selectedPlan) return;
    setSaving(true);
    try {
      // Récupérer les slots S1 de l'employé
      const s1Slots = (selectedPlan.slots || []).filter(
        (s) => s.employeeId === selectedEmpId && s.weekNumber === 1,
      );
      if (s1Slots.length === 0) {
        toast.error("Aucun créneau en S1 à copier");
        setSaving(false);
        return;
      }

      const newSlots = [];
      for (let toWeek = 2; toWeek <= selectedPlan.nbSemaines; toWeek++) {
        for (const s of s1Slots) {
          const res = await upsertSchedulePlanSlot(selectedPlanId, {
            employeeId: selectedEmpId,
            weekNumber: toWeek,
            dayOfWeek: s.dayOfWeek,
            startTime: s.startTime,
            endTime: s.endTime,
            pauseMinutes: s.pauseMinutes,
          });
          newSlots.push({ week: toWeek, day: s.dayOfWeek, slot: res.data });
        }
      }

      setPlans((ps) =>
        ps.map((p) => {
          if (p.id !== selectedPlanId) return p;
          let slots = (p.slots || []).filter(
            (s) => !(s.employeeId === selectedEmpId && s.weekNumber > 1),
          );
          slots = [...slots, ...newSlots.map((n) => n.slot)];
          return { ...p, slots };
        }),
      );
      toast.success(`S1 appliquée à toutes les semaines du cycle`);
    } catch {
      toast.error("Erreur duplication du cycle");
    } finally {
      setSaving(false);
    }
  }

  // ── Copier horaires d'un employé vers un autre ────────────────────────────

  async function handleCopierEmp() {
    if (!selectedPlanId || !selectedEmpId || !copyTargetEmpId) return;
    setModal(null);
    setSaving(true);
    try {
      const sourceSlots = (selectedPlan?.slots || []).filter(
        (s) => s.employeeId === selectedEmpId,
      );
      if (sourceSlots.length === 0) {
        toast.error("Aucun créneau à copier pour cet employé");
        setSaving(false);
        return;
      }

      const newSlots = [];
      for (const s of sourceSlots) {
        const res = await upsertSchedulePlanSlot(selectedPlanId, {
          employeeId: copyTargetEmpId,
          weekNumber: s.weekNumber,
          dayOfWeek: s.dayOfWeek,
          startTime: s.startTime,
          endTime: s.endTime,
          pauseMinutes: s.pauseMinutes,
        });
        newSlots.push(res.data);
      }

      setPlans((ps) =>
        ps.map((p) => {
          if (p.id !== selectedPlanId) return p;
          const slots = (p.slots || []).filter(
            (s) =>
              !(
                s.employeeId === copyTargetEmpId &&
                sourceSlots.some(
                  (src) =>
                    src.weekNumber === s.weekNumber &&
                    src.dayOfWeek === s.dayOfWeek,
                )
              ),
          );
          return { ...p, slots: [...slots, ...newSlots] };
        }),
      );

      const target = employees.find((e) => e.id === copyTargetEmpId);
      toast.success(
        `Horaires copiés vers ${target?.firstName} ${target?.lastName}`,
      );
      setCopyTargetEmpId("");
    } catch {
      toast.error("Erreur copie des horaires");
    } finally {
      setSaving(false);
    }
  }

  // ── Créer plan ────────────────────────────────────────────────────────────

  async function handleCreate(form) {
    const conflict = findOverlap(form.dateDebut, form.dateFin, plans);
    if (conflict) {
      toast.warning(`Ces dates chevauchent « ${conflict.nom} » — ajustez les dates.`);
      return;
    }
    setModal(null);
    try {
      const res = await createSchedulePlan({ ...form, structureId });
      setPlans((ps) => [...ps, res.data]);
      setSelectedPlanId(res.data.id);
      toast.success("Plan créé");
    } catch {
      toast.error("Erreur création du plan");
    }
  }

  // ── Dupliquer plan ────────────────────────────────────────────────────────

  async function handleDuplicate(form) {
    setModal(null);
    if (!selectedPlanId) return;
    try {
      const res = await duplicateSchedulePlan(selectedPlanId, form);
      setPlans((ps) => [...ps, res.data]);
      setSelectedPlanId(res.data.id);
      toast.success("Plan dupliqué");
    } catch {
      toast.error("Erreur duplication du plan");
    }
  }

  // ── Supprimer plan ────────────────────────────────────────────────────────

  async function handleDelete() {
    setModal(null);
    if (!selectedPlanId) return;
    try {
      await deleteSchedulePlan(selectedPlanId);
      const remaining = plans.filter((p) => p.id !== selectedPlanId);
      setPlans(remaining);
      setSelectedPlanId(remaining[0]?.id ?? null);
      toast.success("Plan supprimé");
    } catch {
      toast.error("Erreur suppression du plan");
    }
  }

  // ── Valider / repasser en brouillon ──────────────────────────────────────

  async function handleValidate() {
    if (!selectedPlanId) return;
    try {
      await validateSchedulePlan(selectedPlanId);
      const res = await getSchedulePlans(structureId);
      setPlans(res.data.plans);
      setActivePlanId(res.data.activePlanId);
      toast.success(`Plan « ${selectedPlan?.nom} » validé`);
    } catch {
      toast.error("Erreur lors de la validation du plan");
    }
  }

  async function handleUnvalidate() {
    if (!selectedPlanId) return;
    try {
      await unvalidateSchedulePlan(selectedPlanId);
      const res = await getSchedulePlans(structureId);
      setPlans(res.data.plans);
      setActivePlanId(res.data.activePlanId);
      toast.success("Plan repassé en brouillon");
    } catch {
      toast.error("Erreur lors du passage en brouillon");
    }
  }

  // ── Export PDF semaine ────────────────────────────────────────────────────

  async function handleExportPdf(weekNumber) {
    if (!selectedPlan) return;
    setPdfWeekPicker(false);
    setPdfExporting(true);
    try {
      const { generatePlanningWeekPdf } = await import('../../utils/planningWeekPdf');
      await generatePlanningWeekPdf({
        plan: selectedPlan,
        employees,
        weekNumber,
        structureName,
      });
    } catch {
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setPdfExporting(false);
    }
  }

  // ── Stats employé sélectionné ─────────────────────────────────────────────

  const empSlots = selectedPlan
    ? (selectedPlan.slots || []).filter((s) => s.employeeId === selectedEmpId)
    : [];
  const { totalStr, moyStr } = calcTotalCycle(
    empSlots,
    selectedPlan?.nbSemaines || 1,
  );

  // ── Nombre d'employés configurés dans le plan ─────────────────────────────

  const nbEmpsConfigures = selectedPlan
    ? new Set((selectedPlan.slots || []).map((s) => s.employeeId)).size
    : 0;

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
        Chargement du planning…
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-gray-400">
        <CalendarDays size={40} className="opacity-20" />
        <p className="text-sm">Aucun plan de planning pour cette structure.</p>
        <Btn
          variant="primary"
          size="sm"
          loading={initLoading}
          onClick={handleInit}
        >
          <Plus size={14} /> Créer le plan de référence
        </Btn>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex gap-4 items-start">
        {/* ── Sidebar plans ── */}
        <div className="w-60 shrink-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
          <div
            className="flex items-center gap-2 px-4 py-3 border-b border-gray-100"
            style={{
              background:
                "linear-gradient(to right, rgb(var(--color-primary) / 0.07), transparent)",
            }}
          >
            <CalendarDays size={14} className="text-primary shrink-0" />
            <span className="font-semibold text-sm text-gray-800 flex-1">
              Plannings
            </span>
            <span className="text-xs text-gray-400">{plans.length}</span>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {plans.map((plan) => {
              const isActive = plan.id === activePlanId;
              const isSelected = plan.id === selectedPlanId;
              const nbEmps = new Set(
                (plan.slots || []).map((s) => s.employeeId),
              ).size;
              return (
                <button
                  key={plan.id}
                  onClick={() => setSelectedPlanId(plan.id)}
                  className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
                    isActive
                      ? isSelected
                        ? "border-green-400 bg-green-50 ring-1 ring-green-300/60"
                        : "border-green-300 bg-green-50/60 hover:bg-green-50"
                      : isSelected
                        ? "border-primary/40 bg-primary/5"
                        : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${isActive ? "bg-green-500" : isSelected ? "bg-primary" : "bg-gray-300"}`}
                    />
                    <span className="font-medium text-xs text-gray-800 flex-1 truncate">
                      {plan.nom}
                    </span>
                    {isActive && (
                      <Badge color="bg-green-100 text-green-700">✓ Actif</Badge>
                    )}
                    {plan.isDraft && (
                      <Badge color="bg-gray-100 text-gray-500">Brouillon</Badge>
                    )}
                  </div>
                  {plan.isReference ? (
                    plan.dateFin ? (
                      <p className="text-[10px] text-gray-400 mt-1 ml-4">
                        {fmtDateFr(plan.dateDebut)} → {fmtDateFr(plan.dateFin)}
                      </p>
                    ) : plan.dateDebut ? (
                      <p className="text-[10px] text-gray-400 mt-1 ml-4">
                        À partir du {fmtDateFr(plan.dateDebut)}
                      </p>
                    ) : (
                      <p className="text-[10px] text-gray-400 mt-1 ml-4">
                        Permanent · sans échéance
                      </p>
                    )
                  ) : (
                    plan.dateDebut && (
                      <p className="text-[10px] text-gray-400 mt-1 ml-4">
                        {fmtDateFr(plan.dateDebut)} → {fmtDateFr(plan.dateFin)}
                      </p>
                    )
                  )}
                  {!plan.isReference && plan.dateFin && (() => {
                    const days = Math.ceil((new Date(plan.dateFin) - new Date()) / 864e5);
                    if (days < 0) return (
                      <p className="text-[10px] text-red-500 ml-4 flex items-center gap-1">
                        <AlertTriangle size={9} /> Plan expiré
                      </p>
                    );
                    if (days <= 30) return (
                      <p className={`text-[10px] ml-4 flex items-center gap-1 ${days <= 7 ? "text-red-500" : "text-amber-500"}`}>
                        <AlertTriangle size={9} /> Expire dans {days} jour{days > 1 ? "s" : ""}
                      </p>
                    );
                    return null;
                  })()}
                  <p className="text-[10px] text-gray-400 ml-4">
                    {plan.nbSemaines} sem. · {nbEmps} employé
                    {nbEmps > 1 ? "s" : ""} configuré{nbEmps > 1 ? "s" : ""}
                  </p>
                </button>
              );
            })}
          </div>

          <div className="p-2 border-t border-gray-100 space-y-1.5">
            <Btn
              variant="primary"
              size="sm"
              className="w-full justify-center"
              onClick={() => setModal("nouveau")}
            >
              <Plus size={13} /> Nouveau plan
            </Btn>
            <div className="flex gap-1.5">
              <Btn
                variant="secondary"
                size="sm"
                className="flex-1 justify-center"
                disabled={!selectedPlanId}
                onClick={() => setModal("dupliquer")}
              >
                <Copy size={13} /> Dupliquer
              </Btn>
              <Btn
                variant="danger"
                size="sm"
                disabled={!selectedPlanId || selectedPlan?.isReference}
                onClick={() => setModal("confirmerSuppr")}
              >
                <Trash2 size={13} />
              </Btn>
            </div>
            {selectedPlan?.isDraft && (
              <Btn
                variant="primary"
                size="sm"
                className="w-full justify-center"
                onClick={handleValidate}
              >
                <Check size={13} /> Valider le planning
              </Btn>
            )}
            {selectedPlan && !selectedPlan.isDraft && !selectedPlan.isReference && (
              <Btn
                variant="ghost"
                size="sm"
                className="w-full justify-center text-gray-400 hover:text-gray-600"
                onClick={handleUnvalidate}
              >
                Repasser en brouillon
              </Btn>
            )}
          </div>
        </div>

        {/* ── Éditeur plan + employé ── */}
        <div className="flex-1 min-w-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden">
          {selectedPlan ? (
            <>
              {/* Header plan */}
              <div
                className="px-5 py-3 border-b border-gray-100 space-y-3"
                style={{
                  background:
                    "linear-gradient(to right, rgb(var(--color-primary) / 0.05), transparent)",
                }}
              >
                {/* Ligne 1 : nom + badge + saving */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setEditNom(true)}
                    className="text-gray-300 hover:text-gray-500 transition-colors shrink-0"
                  >
                    <Edit2 size={13} />
                  </button>
                  {editNom ? (
                    <input
                      autoFocus
                      className="text-sm font-semibold text-gray-800 bg-transparent border-b border-dashed border-primary focus:outline-none px-0 flex-1"
                      value={nomEdit}
                      onChange={(e) => setNomEdit(e.target.value)}
                      onBlur={handleNomBlur}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.target.blur();
                      }}
                    />
                  ) : (
                    <span
                      className="text-sm font-semibold text-gray-800 flex-1 cursor-pointer hover:text-primary transition-colors"
                      onClick={() => setEditNom(true)}
                    >
                      {selectedPlan.nom}
                    </span>
                  )}
                  {selectedPlan.isReference && (
                    <Badge color="bg-primary/10 text-primary">Référence</Badge>
                  )}
                  {saving && (
                    <span className="text-[10px] text-primary animate-pulse ml-auto">
                      Sauvegarde…
                    </span>
                  )}
                  <div className="relative ml-auto">
                    <Btn
                      variant="ghost"
                      size="sm"
                      loading={pdfExporting}
                      onClick={() =>
                        selectedPlan.nbSemaines === 1
                          ? handleExportPdf(1)
                          : setPdfWeekPicker((v) => !v)
                      }
                      title="Exporter le planning en PDF"
                    >
                      <FileDown size={14} />
                      <span className="text-xs">PDF semaine</span>
                    </Btn>
                    {pdfWeekPicker && (
                      <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-2 flex flex-col gap-1 min-w-[120px]">
                        <p className="text-[10px] text-gray-400 px-1 mb-0.5">Semaine du cycle</p>
                        {Array.from({ length: selectedPlan.nbSemaines }, (_, i) => i + 1).map((w) => (
                          <button
                            key={w}
                            onClick={() => handleExportPdf(w)}
                            className="text-left text-xs px-3 py-1.5 rounded hover:bg-primary/10 hover:text-primary transition-colors"
                          >
                            Semaine {w}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Ligne 2 : cycle + dates */}
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Timer size={12} className="text-gray-400" />
                    <span className="text-xs text-gray-500">Cycle :</span>
                    <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                      {[1, 2, 3, 4].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => handleUpdate({ nbSemaines: n })}
                          className={`px-2.5 py-1 text-xs font-medium transition-colors border-r border-gray-200 last:border-0 ${selectedPlan.nbSemaines === n ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                        >
                          {n}S
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500">
                      {selectedPlan.isReference ? "À partir du" : "Du"}
                    </span>
                    <input
                      type="date"
                      className={`${inputCls} !w-auto text-xs`}
                      value={toInputDate(selectedPlan.dateDebut)}
                      onChange={(e) =>
                        handleUpdate({ dateDebut: e.target.value || null })
                      }
                    />
                    <span className="text-xs text-gray-500">au</span>
                    <input
                      type="date"
                      className={`${inputCls} !w-auto text-xs`}
                      value={toInputDate(selectedPlan.dateFin)}
                      onChange={(e) =>
                        handleUpdate({ dateFin: e.target.value || null })
                      }
                    />
                    {selectedPlan.isReference && !selectedPlan.dateFin && (
                      <span className="text-[10px] text-gray-400 italic">
                        (permanent si vide)
                      </span>
                    )}
                  </div>
                </div>

                {/* Ligne 3 : sélecteur employé — boutons + copier */}
                <div className="flex items-center gap-2 flex-wrap">
                  <User size={12} className="text-gray-400 shrink-0" />
                  <div className="flex flex-wrap gap-1.5">
                    {employees.map((emp) => {
                      const hasSlots = (selectedPlan.slots || []).some(
                        (s) => s.employeeId === emp.id,
                      );
                      const isSelected = emp.id === selectedEmpId;
                      return (
                        <button
                          key={emp.id}
                          onClick={() => setSelectedEmpId(emp.id)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                            isSelected
                              ? "bg-primary text-white border-primary shadow-sm"
                              : hasSlots
                                ? "bg-primary/8 text-primary border-primary/25 hover:bg-primary/15"
                                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          {emp.firstName} {emp.lastName}
                          {hasSlots && !isSelected && (
                            <span className="ml-1 text-primary/60">✓</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {selectedEmpId && empSlots.length > 0 && (
                    <>
                      <span className="text-xs text-gray-400 ml-1">
                        {totalStr} / cycle · {moyStr}/sem.
                      </span>
                      {employees.length > 1 && (
                        <button
                          onClick={() => {
                            setCopyTargetEmpId("");
                            setModal("copierEmp");
                          }}
                          title="Copier ces horaires vers un autre employé"
                          className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-primary transition-colors px-2 py-1 rounded border border-gray-200 hover:border-primary/30 hover:bg-primary/5"
                        >
                          <CopyPlus size={11} /> Copier vers…
                        </button>
                      )}
                    </>
                  )}
                  {nbEmpsConfigures > 0 && (
                    <span className="text-[10px] text-gray-400 ml-auto">
                      {nbEmpsConfigures}/{employees.length} employé
                      {nbEmpsConfigures > 1 ? "s" : ""} configuré
                      {nbEmpsConfigures > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>

              {/* Grille */}
              <div className="flex-1 overflow-auto p-5">
                {selectedEmpId ? (
                  <GrilleHoraires
                    plan={selectedPlan}
                    employeeId={selectedEmpId}
                    onCellClick={(semIndex, jourIndex, slot) =>
                      setSlotModal({ semIndex, jourIndex, slot })
                    }
                    onDupliquerSemaine={handleDupliquerSemaine}
                    onDupliquerToutCycle={handleDupliquerToutCycle}
                  />
                ) : (
                  <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                    Sélectionnez un employé ci-dessus pour éditer ses horaires
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
              Sélectionnez un plan ou créez-en un nouveau
            </div>
          )}
        </div>
      </div>

      {/* Timeline */}
      {plans.length > 0 && (
        <PlanTimeline plans={plans} activePlanId={activePlanId} />
      )}

      {/* Modale créneau */}
      {slotModal && (
        <ModalCreneau
          semIndex={slotModal.semIndex}
          jourIndex={slotModal.jourIndex}
          creneau={slotModal.slot}
          onSave={handleSlotSave}
          onDelete={handleSlotDelete}
          onClose={() => setSlotModal(null)}
        />
      )}

      {modal === "nouveau" && (
        <ModalNouveauPlan
          onConfirm={handleCreate}
          onClose={() => setModal(null)}
          existingPlans={plans}
        />
      )}
      {modal === "dupliquer" && selectedPlan && (
        <ModalDupliquerPlan
          plan={selectedPlan}
          onConfirm={handleDuplicate}
          onClose={() => setModal(null)}
        />
      )}

      {modal === "confirmerSuppr" && selectedPlan && (
        <Modal
          title="Supprimer le plan"
          size="sm"
          onClose={() => setModal(null)}
          footer={
            <div className="flex gap-2">
              <Btn variant="secondary" size="sm" onClick={() => setModal(null)}>
                Annuler
              </Btn>
              <Btn variant="danger" size="sm" onClick={handleDelete}>
                <Trash2 size={13} /> Supprimer
              </Btn>
            </div>
          }
        >
          <p className="text-sm text-gray-600">
            Supprimer <strong>"{selectedPlan.nom}"</strong> et tous ses créneaux
            (tous les employés) ? Action irréversible.
          </p>
        </Modal>
      )}

      {modal === "copierEmp" && selectedEmpId && (
        <Modal
          title="Copier les horaires vers…"
          size="sm"
          onClose={() => setModal(null)}
          footer={
            <div className="flex gap-2">
              <Btn variant="secondary" size="sm" onClick={() => setModal(null)}>
                Annuler
              </Btn>
              <Btn
                variant="primary"
                size="sm"
                disabled={!copyTargetEmpId}
                onClick={handleCopierEmp}
              >
                <CopyPlus size={13} /> Copier
              </Btn>
            </div>
          }
        >
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Les horaires de{" "}
              <strong>
                {employees.find((e) => e.id === selectedEmpId)?.firstName}
              </strong>{" "}
              seront copiés vers :
            </p>
            <div className="flex flex-wrap gap-2">
              {employees
                .filter((e) => e.id !== selectedEmpId)
                .map((emp) => (
                  <button
                    key={emp.id}
                    onClick={() => setCopyTargetEmpId(emp.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${copyTargetEmpId === emp.id ? "bg-primary text-white border-primary" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                  >
                    {emp.firstName} {emp.lastName}
                  </button>
                ))}
            </div>
            <p className="text-xs text-gray-400">
              Les créneaux existants de l'employé cible seront remplacés.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}
